import { Browserbase } from 'browserbase';
import { clearMemoized, memoize } from '../worker/memoize';
import { concurrency } from './concurrency';
export class AgreeableDatabase extends Browserbase {
    docDBs = new Map();
    constructor(name) {
        if (!name.startsWith('agreeable/')) {
            name = `agreeable/${name}`;
        }
        // Don't dispatch changes across tabs since we will be handling that ourselves
        super(name, { dontDispatch: true });
        this.version(1, {
            docs: 'path',
            changes: '[path+rev], [path+committed+rev], committed',
            deleted: 'path',
        });
    }
    doc(path) {
        let docDb = this.docDBs.get(path);
        if (!docDb) {
            docDb = docDB(this, path);
            this.docDBs.set(path, docDb);
        }
        return docDb;
    }
}
export function agreeableDatabase(uid) {
    return new AgreeableDatabase(uid);
}
function docDB(db, path, isTransaction) {
    const { blocking } = concurrency(path);
    const api = {
        start,
        commit,
        getDoc: blocking(memoize(getDoc)),
        putDoc: blocking(clearMemoized(putDoc, getDoc)),
        markDocForDelete: blocking(clearMemoized(markDocForDelete, getDoc)),
        deleteDoc: blocking(clearMemoized(deleteDoc, getDoc)),
        getChanges: blocking(memoize(getChanges, 1, 10000)),
        getChangeCount: blocking(getChangeCount),
        putChanges: blocking(clearMemoized(putChanges, getChanges)),
        deleteChanges: blocking(clearMemoized(deleteChanges, getChanges)),
        getLastRev: blocking(getLastRev),
    };
    return api;
    function start() {
        return docDB(db.start(), path, true);
    }
    function commit() {
        if (!isTransaction) {
            throw new Error('commit() must be called on a transaction');
        }
        return db.commit();
    }
    async function getDoc() {
        return db.stores.docs.get(path);
    }
    async function putDoc(doc, rev) {
        const old = doc;
        const docRecord = { path, doc, rev };
        try {
            await db.stores.docs.put(docRecord);
        }
        catch (err) {
            doc = old;
            throw err;
        }
    }
    async function markDocForDelete(path) {
        const transaction = db.start();
        const toDelete = (await Promise.all([
            transaction.stores.docs.get(path),
            transaction.stores.docs
                .where()
                .startsWith(path + '/')
                .getAll(),
        ])).flat();
        transaction.stores.deleted.put({ path });
        toDelete.forEach(({ path }) => {
            transaction.stores.docs.delete(path);
            changesWhere(transaction, path).deleteAll();
        });
        await transaction.commit();
        return toDelete.map(({ path }) => path);
    }
    async function deleteDoc() {
        const transaction = db.start();
        transaction.stores.deleted.delete(path);
        transaction.stores.docs.delete(path);
        changesWhere(transaction, path).deleteAll();
        await transaction.commit();
    }
    async function getChanges(options) {
        const query = changesWhere(db, path, options);
        return query.getAll().then(c => c.map(({ path, ...c }) => c));
    }
    async function getChangeCount(options) {
        const query = changesWhere(db, path, options);
        return query.count();
    }
    async function putChanges(changes) {
        const records = changes.map(({ committed, ...c }) => ({ path, committed: committed ? 1 : 0, ...c }));
        await db.stores.changes.putAll(records);
    }
    async function deleteChanges(options) {
        if (Array.isArray(options)) {
            await Promise.all(options.map(c => db.stores.changes.delete([path, c.rev])));
        }
        else {
            const query = changesWhere(db, path, options);
            await query.deleteAll();
        }
    }
    async function getLastRev(options) {
        const changes = await api.getChanges({ ...options, reverse: true, limit: 1 });
        if (changes.length)
            return changes[0].rev;
        const doc = await db.stores.docs.get(path);
        return doc?.rev;
    }
}
function changesWhere(db, path, options) {
    const useCommitted = options && 'committed' in options;
    const value = useCommitted ? [path, options.committed ? 1 : 0] : [path];
    let query = db.stores.changes.where(useCommitted ? '[path+committed+rev]' : '').startsWith(value);
    if (!options)
        return query;
    if (options.rev != null)
        query.startsAt([...value, options.rev]).endsAt([...value, options.rev]);
    if (options.before != null)
        query.endsBefore([...value, options.before]);
    if (options.after != null)
        query.startsAfter([...value, options.after]);
    if (options.reverse)
        query.reverse();
    if (options.limit)
        query.limit(options.limit);
    return query;
}
